import { Box, Flex, Text } from '@chakra-ui/react';
import { reactEvents } from '@bridge/reactEvents';
import {
  suppliersApi,
  SupplierSearchQuery,
} from '@texas/api/endpoints/suppliersApi';
import { complianceDocumentDefinitionsApi } from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { ReactSelectOption } from '@texas/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchFilterInput } from '../SearchFilterInput';
import { BodyOverride } from '../shared/BodyOverride';
import { DataTableContainer } from '../shared/dataTable/DataTableContainer';
import { EntityDirectorySelectFilter } from '../shared/entityDirectory/EntityDirectorySelectFilter';
import { EntityDirectoryFilterBar } from '../shared/entityDirectory/EntityDirectoryFilterBar';
import { EntityDirectoryPage } from '../shared/entityDirectory/EntityDirectoryPage';
import { TexasSwitch } from '../shared/form/TexasSwitch';
import { suppliersColumns } from './Columns';
import { ExportSuppliersButton } from './ExportSuppliersButton';
import { NewSupplier } from './newSupplier/newSupplier';
import { SupplierBranchFilter } from './SupplierBranchFilter';
import { SupplierComplianceDocumentFilter } from './SupplierComplianceDocumentFilter';
import { SupplierTypeFilter } from './SupplierTypeFilter';
import { tagsApi } from '@texas/api/endpoints/tagsApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { SupplierTagFilter } from './SupplierTagFilter';
import { tagEvents } from '../tags/events';
import { useSignalSubscriptionEvents } from '../shared/hooks/useSignalSubscriptionEvents';

const defaultSearchQuery: SupplierSearchQuery = {
  sortDesc: false,
  searchTerm: '',
  includeArchived: false,
  page: 1,
  pageSize: 24,
  organizationId: 3,
  sortBy: '',
};

export function Suppliers() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] =
    useState<SupplierSearchQuery>(defaultSearchQuery);
  const {
    branchesData: [branches, getBranches],
  } = useMetadata();

  useEffect(() => {
    getBranches();
  }, [getBranches]);

  const {
    data: suppliers,
    refetch,
    loading,
    error,
  } = useApiResource(suppliersApi.searchSuppliers);
  const {
    data: complianceDocumentDefinitions,
    refetch: refetchComplianceDocumentDefinitions,
    loading: complianceDocumentDefinitionsLoading,
  } = useApiResource(complianceDocumentDefinitionsApi.getAll);
  const {
    data: supplierTags,
    refetch: refetchSupplierTags,
    loading: supplierTagsLoading,
  } = useApiResource(tagsApi.getAll);

  useEffect(() => {
    refetchComplianceDocumentDefinitions({
      searchTerm: '',
      includeArchived: false,
      sortBy: 'kind',
      sortDesc: false,
    });
  }, [refetchComplianceDocumentDefinitions]);

  useEffect(() => {
    refetchSupplierTags({
      includeArchived: false,
      includeSystemTags: false,
      filterByEntityType: EntityType.Supplier,
    });
  }, [refetchSupplierTags]);

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const triggerRefetchAll = useCallback(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  useEffect(() => {
    return reactEvents.supplierCreated.subscribe(() => {
      triggerRefetchAll();
    });
  }, [triggerRefetchAll]);

  useSignalSubscriptionEvents({
    signalEvents: [tagEvents.tagUpdated],
    triggerOnEvent: () => {
      refetchSupplierTags({
        includeArchived: false,
        includeSystemTags: false,
        filterByEntityType: EntityType.Supplier,
      });
      triggerRefetchAll();
    },
  });

  useEffect(
    () =>
      tagEvents.tagLinksUpdated.subscribe((event) => {
        if (event.entityType === EntityType.Supplier) {
          triggerRefetchAll();
        }
      }),
    [triggerRefetchAll],
  );

  const columns = useMemo(() => suppliersColumns(t), [t]);
  const branchOptions = useMemo<ReactSelectOption[]>(
    () =>
      branches.map((branch) => ({
        value: branch.id,
        label: `${branch.name} (${branch.identifier})`,
      })),
    [branches],
  );
  const totalItems = suppliers?.items.totalItems ?? 0;
  const firstItem =
    totalItems === 0 ? 0 : (searchQuery.page - 1) * searchQuery.pageSize + 1;
  const lastItem = Math.min(
    searchQuery.page * searchQuery.pageSize,
    totalItems,
  );

  return (
    <BodyOverride>
      <EntityDirectoryPage
        actions={
          <Flex align="center" gap={2}>
            <ExportSuppliersButton
              supplierIds={suppliers?.supplierIds}
              organizationId={searchQuery.organizationId}
              isDisabled={loading}
              variant="entityDirectorySecondary"
              h="38px"
              px={4}
            >
              {t('general.exportExcel')}
            </ExportSuppliersButton>
            <Box className="supplier-create-action">
              <NewSupplier buttonVariant="entityDirectoryPrimary" />
            </Box>
          </Flex>
        }
        description={t('suppliers.directoryDescription')}
        title={t('general.suppliers')}
      >
        <Flex direction="column" gap={5}>
          <EntityDirectoryFilterBar
            actions={
              <Box flexShrink={0}>
                <TexasSwitch
                  checked={searchQuery.includeArchived}
                  label={t('general.includeArchived')}
                  onChange={(checked) =>
                    setSearchQuery({
                      ...searchQuery,
                      includeArchived: checked,
                      page: 1,
                    })
                  }
                />
              </Box>
            }
          >
            <Box flex="1 1 280px" maxW={{ base: 'full', lg: '340px' }}>
              <SearchFilterInput
                style="entityDirectory"
                value={searchQuery.searchTerm}
                placeholder={t('suppliers.searchSuppliers')}
                onChange={(searchTerm) =>
                  setSearchQuery({ ...searchQuery, searchTerm, page: 1 })
                }
              />
            </Box>
            <SupplierBranchFilter
              options={branchOptions}
              selectedBranchIds={searchQuery.branchIds}
              onChange={(branchIds) =>
                setSearchQuery({ ...searchQuery, branchIds, page: 1 })
              }
            />
            <SupplierTypeFilter
              value={searchQuery.isInternal}
              onChange={(isInternal) =>
                setSearchQuery({ ...searchQuery, isInternal, page: 1 })
              }
            />
            <EntityDirectorySelectFilter
              options={[
                {
                  label: `${t('suppliers.isItlSupplier')}: ${t('general.yes')}`,
                  value: true,
                },
                {
                  label: `${t('suppliers.isItlSupplier')}: ${t('general.no')}`,
                  value: false,
                },
              ]}
              placeholder={`${t('suppliers.isItlSupplier')}: ${t(
                'filter.all',
              )}`}
              value={searchQuery.isItlSupplier}
              onChange={(isItlSupplier) =>
                setSearchQuery({
                  ...searchQuery,
                  isItlSupplier: isItlSupplier ?? undefined,
                  page: 1,
                })
              }
            />
            <SupplierComplianceDocumentFilter
              definitions={complianceDocumentDefinitions ?? []}
              isLoading={complianceDocumentDefinitionsLoading}
              selectedDefinitionIds={
                searchQuery.complianceDocumentDefinitionIds
              }
              onChange={(complianceDocumentDefinitionIds) =>
                setSearchQuery({
                  ...searchQuery,
                  complianceDocumentDefinitionIds,
                  page: 1,
                })
              }
            />
            <SupplierTagFilter
              tags={supplierTags ?? []}
              isLoading={supplierTagsLoading}
              selectedTagIds={searchQuery.tagIds}
              onChange={(tagIds) =>
                setSearchQuery({ ...searchQuery, tagIds, page: 1 })
              }
            />
          </EntityDirectoryFilterBar>

          <Box>
            <DataTableContainer
              bg="entityDirectory.surface"
              border="1px solid"
              borderColor="entityDirectory.border"
              borderRadius={0}
              w="100%"
              datatable={{
                data: suppliers?.items.items ?? [],
                sorted: {
                  onSortedChange: ({ key, desc }) => {
                    setSearchQuery({
                      ...searchQuery,
                      sortBy: key,
                      sortDesc: desc,
                    });
                  },
                  key: searchQuery.sortBy,
                  desc: searchQuery.sortDesc,
                },
                isLoading: loading,
                columns,
                variant: 'entity-directory',
              }}
              error={error}
              footer={
                <Text color="entityDirectory.mutedText" fontSize="11px">
                  {t('suppliers.showingResults', {
                    first: firstItem,
                    last: lastItem,
                    total: totalItems,
                  })}
                </Text>
              }
              pagination={{
                totalItems,
                pageSize: searchQuery.pageSize,
                currentPage: searchQuery.page,
                onPageChange: (page) =>
                  setSearchQuery({
                    ...searchQuery,
                    page,
                  }),
                variant: 'entityDirectoryPagination',
              }}
            />
          </Box>
        </Flex>
      </EntityDirectoryPage>
    </BodyOverride>
  );
}
